// setupSentry.ts
import * as Sentry from '@sentry/react';

/**
 * Initialises the Sentry browser SDK. Mirrors the configuration used by the
 * `web-be` service (see web-be/app/instrument.ts):
 *  - environment + trace sampling driven by the build-time env,
 *  - PII disabled by default,
 *  - the deployment region attached as a tag so EU/US events are separable.
 *
 * The DSN is supplied per environment via REACT_APP_SENTRY_DSN. When it is
 * absent (e.g. local development without a DSN) the SDK is left uninitialised
 * so nothing is sent.
 */
const setupSentry = () => {
  try {
    const dsn = process.env.REACT_APP_SENTRY_DSN;
    if (!dsn) {
      return;
    }

    const environment = process.env.REACT_APP_ENV ?? 'local';
    const region = process.env.REACT_APP_RUM_APPLICATION_REGION;

    Sentry.init({
      dsn,
      environment,
      // The commit the bundle was built from, injected by the deploy workflow.
      // REACT_APP_RUM_APPLICATION_VERSION is the fallback and is a constant
      // (1.0.0) in every .env file, so on its own every event from every build
      // ever shipped carries the same release and none of them can be told
      // apart. A real value is what makes "are users still on the old bundle?"
      // answerable — which is how a risky release gets timed (PLAT-6541).
      release:
        process.env.REACT_APP_BUILD_SHA ||
        process.env.REACT_APP_RUM_APPLICATION_VERSION,
      sendDefaultPii: false,
      // Match web-be: sample 10% of traces in production, everything elsewhere.
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/[^/]*\.themartec\.com/,
      ],
      enableLogs: true,
      integrations: [
        Sentry.browserTracingIntegration(),
        // Forward console.log / warn / error calls to Sentry as logs.
        Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
      ],
      ...(region && {
        initialScope: { tags: { aws_region: region } },
      }),
    });
  } catch (error) {
    // Never let Sentry initialisation break app startup.
    console.error('Error initializing Sentry:', error);
  }
};

export default setupSentry;
